import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from './use-toast';
import { useAuth } from '@/contexts/AuthContext';

// ========== useCoEConfig ==========
interface CoEConfig {
  id?: string;
  organization_id: string;
  vision: string | null;
  objective: string | null;
  value_vision_archetype: string | null;
  selected_principles: string[] | null;
  custom_principles: any | null;
  strategic_plan: string | null;
  prioritized_processes: string[] | null;
  prioritized_challenges: Array<{ title: string; description?: string; expected_ai_outcome?: string; priority?: string }> | null;
  operating_model: string | null;
  objectives: string[] | null;
}

export function useCoEConfig(organizationId: string) {
  const queryClient = useQueryClient();

  const { data: config, isLoading } = useQuery({
    queryKey: ['coe-config', organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('coe_configuration')
        .select('*')
        .eq('organization_id', organizationId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      return data as unknown as CoEConfig | null;
    },
    enabled: !!organizationId && organizationId !== 'all',
  });

  const saveConfig = useMutation({
    mutationFn: async (configData: Partial<CoEConfig>) => {
      const payload = {
        organization_id: organizationId,
        ...configData,
      };

      const { data, error } = await supabase
        .from('coe_configuration')
        .upsert(payload, { onConflict: 'organization_id' })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coe-config', organizationId] });
      toast({
        title: 'Configuración guardada',
        description: 'La configuración estratégica del Centro de Excelencia se ha guardado correctamente.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error al guardar',
        description: error.message || 'No se pudo guardar la configuración.',
        variant: 'destructive',
      });
    },
  });

  return {
    config,
    loading: isLoading,
    saveConfig: saveConfig.mutate,
    isSaving: saveConfig.isPending,
  };
}

// ========== useCoEPolicies ==========
interface CoEPolicy {
  id?: string;
  organization_id: string;
  category: string;
  name: string;
  description: string | null;
  priority: string | null;
  status: string | null;
  notes: string | null;
  is_default: boolean;
  sort_order: number | null;
}

const DEFAULT_POLICIES: Omit<CoEPolicy, 'id' | 'organization_id'>[] = [
  // Transparencia
  { category: 'Transparencia', name: 'Explicabilidad de Modelos', description: 'Todos los modelos de IA deben poder explicar sus decisiones a usuarios afectados', priority: 'Alta', status: 'pending', notes: null, is_default: true, sort_order: 1 },
  { category: 'Transparencia', name: 'Divulgación de Uso de IA', description: 'Informar claramente cuando un servicio o decisión involucra IA', priority: 'Alta', status: 'pending', notes: null, is_default: true, sort_order: 2 },
  { category: 'Transparencia', name: 'Documentación de Modelos', description: 'Mantener model cards y documentación técnica actualizada', priority: 'Media', status: 'pending', notes: null, is_default: true, sort_order: 3 },
  // Accountability
  { category: 'Accountability', name: 'Asignación de Responsabilidades', description: 'Definir claramente quién es responsable de cada sistema de IA', priority: 'Alta', status: 'pending', notes: null, is_default: true, sort_order: 4 },
  { category: 'Accountability', name: 'Auditoría Continua', description: 'Realizar auditorías periódicas de rendimiento y sesgo', priority: 'Alta', status: 'pending', notes: null, is_default: true, sort_order: 5 },
  { category: 'Accountability', name: 'Trazabilidad de Decisiones', description: 'Mantener logs de decisiones para revisión posterior', priority: 'Media', status: 'pending', notes: null, is_default: true, sort_order: 6 },
  // Equidad
  { category: 'Equidad', name: 'Evaluación de Sesgos', description: 'Evaluar y mitigar sesgos en datos y modelos antes del despliegue', priority: 'Alta', status: 'pending', notes: null, is_default: true, sort_order: 7 },
  { category: 'Equidad', name: 'Impacto en Grupos Vulnerables', description: 'Analizar impacto diferencial en grupos protegidos', priority: 'Alta', status: 'pending', notes: null, is_default: true, sort_order: 8 },
  { category: 'Equidad', name: 'Accesibilidad', description: 'Garantizar que los sistemas de IA sean accesibles para todos', priority: 'Media', status: 'pending', notes: null, is_default: true, sort_order: 9 },
];

export function useCoEPolicies(organizationId: string) {
  const queryClient = useQueryClient();

  const { data: policies = [], isLoading } = useQuery({
    queryKey: ['coe-policies', organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('coe_policies')
        .select('*')
        .eq('organization_id', organizationId)
        .order('sort_order');

      if (error) throw error;

      // Si no hay políticas, insertar las predeterminadas
      if (!data || data.length === 0) {
        const defaultPolicies = DEFAULT_POLICIES.map(p => ({
          ...p,
          organization_id: organizationId,
        }));

        const { data: inserted, error: insertError } = await supabase
          .from('coe_policies')
          .insert(defaultPolicies)
          .select();

        if (insertError) throw insertError;
        return inserted as CoEPolicy[];
      }

      return data as CoEPolicy[];
    },
    enabled: !!organizationId && organizationId !== 'all',
  });

  const addPolicy = useMutation({
    mutationFn: async (policy: Omit<CoEPolicy, 'id' | 'organization_id'>) => {
      const { data, error } = await supabase
        .from('coe_policies')
        .insert({ ...policy, organization_id: organizationId })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coe-policies', organizationId] });
      toast({ title: 'Política agregada', description: 'La política se ha agregado correctamente.' });
    },
  });

  const toggleStatus = useMutation({
    mutationFn: async ({ id, currentStatus }: { id: string; currentStatus: string | null }) => {
      const newStatus = currentStatus === 'implemented' ? 'pending' : 'implemented';
      const { error } = await supabase
        .from('coe_policies')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coe-policies', organizationId] });
    },
  });

  const updateNotes = useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes: string }) => {
      const { error } = await supabase
        .from('coe_policies')
        .update({ notes })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coe-policies', organizationId] });
      toast({ title: 'Notas actualizadas', description: 'Las notas de la política se han actualizado.' });
    },
  });

  const deletePolicy = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('coe_policies')
        .delete()
        .eq('id', id)
        .eq('is_default', false); // Solo eliminar custom policies

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coe-policies', organizationId] });
      toast({ title: 'Política eliminada', description: 'La política se ha eliminado correctamente.' });
    },
  });

  return {
    policies,
    loading: isLoading,
    addPolicy: addPolicy.mutate,
    toggleStatus: toggleStatus.mutate,
    updateNotes: updateNotes.mutate,
    deletePolicy: deletePolicy.mutate,
  };
}

// ========== useCoERegulatoryCompliance ==========
interface RegulatoryCompliance {
  id?: string;
  organization_id: string;
  framework_name: string;
  requirement_key: string;
  label: string | null;
  description: string | null;
  priority: string | null;
  is_compliant: boolean | null;
  is_custom: boolean;
  is_active: boolean;
  notes: string | null;
}

const REGULATORY_FRAMEWORKS = [
  {
    framework: 'EU AI Act',
    requirements: [
      { key: 'ai_act_classification', label: 'Clasificación de sistemas por nivel de riesgo' },
      { key: 'ai_act_high_risk', label: 'Requisitos para sistemas de alto riesgo' },
      { key: 'ai_act_prohibited', label: 'Prohibición de prácticas inaceptables' },
      { key: 'ai_act_transparency', label: 'Obligaciones de transparencia para chatbots y deepfakes' },
      { key: 'ai_act_human_oversight', label: 'Supervisión humana de sistemas de alto riesgo' },
      { key: 'ai_act_data_quality', label: 'Calidad de datos de entrenamiento, validación y prueba' },
      { key: 'ai_act_technical_docs', label: 'Documentación técnica completa antes de comercialización' },
      { key: 'ai_act_logging', label: 'Registro automático de eventos (logging) durante operación' },
      { key: 'ai_act_conformity', label: 'Evaluación de conformidad para sistemas de alto riesgo' },
      { key: 'ai_act_eu_database', label: 'Registro en base de datos de la UE para sistemas de alto riesgo' },
      { key: 'ai_act_cybersecurity', label: 'Robustez, precisión y ciberseguridad de sistemas' },
      { key: 'ai_act_sandbox', label: 'Participación en sandboxes regulatorios de IA' },
    ],
  },
  {
    framework: 'GDPR',
    requirements: [
      { key: 'gdpr_legal_basis', label: 'Base legal para procesamiento de datos personales' },
      { key: 'gdpr_automated_decisions', label: 'Derecho a no ser sujeto de decisiones automatizadas (Art. 22)' },
      { key: 'gdpr_dpia', label: 'Evaluaciones de impacto en protección de datos (DPIA)' },
      { key: 'gdpr_privacy_by_design', label: 'Privacy by design y by default' },
      { key: 'gdpr_data_subject_rights', label: 'Derechos del interesado (acceso, rectificación, supresión, portabilidad)' },
      { key: 'gdpr_dpo', label: 'Designación de Delegado de Protección de Datos (DPO)' },
      { key: 'gdpr_international_transfers', label: 'Transferencias internacionales de datos con garantías adecuadas' },
      { key: 'gdpr_breach_notification', label: 'Notificación de brechas de seguridad en 72 horas' },
      { key: 'gdpr_processing_records', label: 'Registro de actividades de tratamiento de datos' },
      { key: 'gdpr_consent_management', label: 'Gestión de consentimiento para procesamiento con IA' },
    ],
  },
  {
    framework: 'ISO/IEC 42001',
    requirements: [
      { key: 'iso_management_system', label: 'Sistema de gestión de IA (AIMS) documentado' },
      { key: 'iso_governance', label: 'Gobernanza y responsabilidad de la alta dirección' },
      { key: 'iso_risk_management', label: 'Gestión de riesgos específica para IA' },
      { key: 'iso_continuous_improvement', label: 'Mejora continua de prácticas de IA' },
      { key: 'iso_ai_policy', label: 'Política de IA formal aprobada por la dirección (A.2)' },
      { key: 'iso_internal_org', label: 'Organización interna para gestión de IA (A.3)' },
      { key: 'iso_resources', label: 'Recursos asignados para sistemas de IA (A.4)' },
      { key: 'iso_impact_assessment', label: 'Evaluación de impacto de sistemas de IA (A.5)' },
      { key: 'iso_lifecycle', label: 'Gestión del ciclo de vida de sistemas de IA (A.6)' },
      { key: 'iso_data_management', label: 'Gestión de datos para sistemas de IA (A.7)' },
      { key: 'iso_stakeholder_info', label: 'Información para partes interesadas (A.8)' },
      { key: 'iso_ai_usage', label: 'Políticas de uso de sistemas de IA (A.9)' },
      { key: 'iso_third_parties', label: 'Gestión de proveedores y terceros de IA (A.10)' },
      { key: 'iso_internal_audit', label: 'Auditoría interna del sistema de gestión de IA' },
      { key: 'iso_competence', label: 'Competencia y formación del personal en IA' },
    ],
  },
  // ===== MARCOS COLOMBIANOS =====
  {
    framework: 'Ley 1581/2012 — Habeas Data',
    requirements: [
      { key: 'ley1581_registro_sic', label: 'Registro de bases de datos ante la SIC' },
      { key: 'ley1581_autorizacion', label: 'Autorización previa del titular para tratamiento de datos' },
      { key: 'ley1581_politica_tratamiento', label: 'Política de tratamiento de datos personales publicada' },
      { key: 'ley1581_oficial_proteccion', label: 'Oficial de protección de datos designado' },
      { key: 'ley1581_aviso_privacidad', label: 'Aviso de privacidad conforme al Decreto 1377/2013' },
      { key: 'ley1581_datos_sensibles', label: 'Protección reforzada de datos sensibles y de menores' },
      { key: 'ley1581_transferencia_intl', label: 'Transferencia internacional de datos con autorización SIC' },
      { key: 'ley1581_consultas_reclamos', label: 'Procedimiento de consultas y reclamos del titular' },
      { key: 'ley1581_incidentes', label: 'Reporte de incidentes de seguridad a la SIC' },
      { key: 'ley1581_ia_decisiones', label: 'Evaluación de impacto en tratamientos con IA y decisiones automatizadas' },
    ],
  },
  {
    framework: 'CONPES 4144/2025 — Política Nacional de IA',
    requirements: [
      { key: 'conpes_riesgos', label: 'Evaluación de riesgos de sistemas de IA según niveles de impacto' },
      { key: 'conpes_principios_eticos', label: 'Principios éticos de IA (transparencia, equidad, rendición de cuentas)' },
      { key: 'conpes_gobernanza', label: 'Gobernanza organizacional para adopción responsable de IA' },
      { key: 'conpes_talento', label: 'Estrategia de talento y capacitación en IA' },
      { key: 'conpes_interoperabilidad', label: 'Interoperabilidad y estándares abiertos' },
      { key: 'conpes_sandbox', label: 'Sandbox regulatorio y experimentación controlada' },
      { key: 'conpes_impacto_social', label: 'Monitoreo de impacto social y ambiental de sistemas de IA' },
    ],
  },
  {
    framework: 'Marco Ético MinTIC',
    requirements: [
      { key: 'mintic_transparencia', label: 'Transparencia algorítmica y explicabilidad' },
      { key: 'mintic_no_discriminacion', label: 'No discriminación y equidad en decisiones automatizadas' },
      { key: 'mintic_supervision', label: 'Supervisión humana significativa' },
      { key: 'mintic_rendicion', label: 'Rendición de cuentas y trazabilidad' },
      { key: 'mintic_derechos_digitales', label: 'Protección de derechos fundamentales digitales' },
    ],
  },
  {
    framework: 'Circular SIC 002/2024 — IA y Datos Personales',
    requirements: [
      { key: 'sic2024_principios', label: 'Principios de idoneidad, necesidad, razonabilidad y proporcionalidad' },
      { key: 'sic2024_accountability', label: 'Responsabilidad demostrada (accountability) en tratamiento con IA' },
      { key: 'sic2024_privacy_design', label: 'Privacidad desde el diseño en desarrollo de sistemas IA' },
      { key: 'sic2024_riesgos', label: 'Evaluación de riesgos asociados al tratamiento de datos con IA' },
      { key: 'sic2024_seguridad', label: 'Medidas de seguridad técnicas, humanas y administrativas' },
      { key: 'sic2024_autorizacion', label: 'Autorización informada sobre uso de IA en tratamiento de datos' },
      { key: 'sic2024_monitoreo', label: 'Monitoreo continuo de sistemas de IA que procesan datos personales' },
    ],
  },
  {
    framework: 'Circular SIC 002/2025 — Transferencia de Tecnología',
    requirements: [
      { key: 'sic2025_riesgos_transf', label: 'Evaluación previa de riesgos en transferencia de tecnología con datos personales' },
      { key: 'sic2025_garantias', label: 'Garantías contractuales para protección de datos en transferencia' },
      { key: 'sic2025_inventario', label: 'Inventario de activos tecnológicos que tratan datos personales' },
      { key: 'sic2025_due_diligence', label: 'Due diligence del receptor de la tecnología' },
      { key: 'sic2025_notificacion', label: 'Notificación a la SIC cuando la transferencia incluya datos sensibles' },
      { key: 'sic2025_contingencia', label: 'Planes de contingencia y reversión de transferencia' },
    ],
  },
];

export function useCoERegulatoryCompliance(organizationId: string) {
  const queryClient = useQueryClient();

  const { data: compliance = [], isLoading } = useQuery({
    queryKey: ['coe-regulatory-compliance', organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('coe_regulatory_compliance')
        .select('*')
        .eq('organization_id', organizationId);

      if (error) throw error;

      // Si no hay registros, inicializar desde constantes
      if (!data || data.length === 0) {
        const allRequirements: Omit<RegulatoryCompliance, 'id'>[] = [];
        REGULATORY_FRAMEWORKS.forEach(framework => {
          framework.requirements.forEach(req => {
            allRequirements.push({
              organization_id: organizationId,
              framework_name: framework.framework,
              requirement_key: req.key,
              label: req.label,
              description: null,
              priority: null,
              is_compliant: false,
              is_custom: false,
              is_active: true,
              notes: null,
            });
          });
        });

        const { data: inserted, error: insertError } = await supabase
          .from('coe_regulatory_compliance')
          .insert(allRequirements)
          .select();

        if (insertError) throw insertError;
        return inserted as RegulatoryCompliance[];
      }

      return data as RegulatoryCompliance[];
    },
    enabled: !!organizationId && organizationId !== 'all',
  });

  const toggleCompliance = useMutation({
    mutationFn: async ({ id, isCompliant }: { id: string; isCompliant: boolean | null }) => {
      const { error } = await supabase
        .from('coe_regulatory_compliance')
        .update({ is_compliant: !isCompliant, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coe-regulatory-compliance', organizationId] });
    },
  });

  const updateNotes = useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes: string }) => {
      const { error } = await supabase
        .from('coe_regulatory_compliance')
        .update({ notes, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coe-regulatory-compliance', organizationId] });
      toast({ title: 'Notas actualizadas', description: 'Las notas se han actualizado correctamente.' });
    },
  });

  const addRequirement = useMutation({
    mutationFn: async (req: { framework_name: string; label: string; description?: string; priority?: string }) => {
      const { data, error } = await supabase
        .from('coe_regulatory_compliance')
        .insert({
          organization_id: organizationId,
          framework_name: req.framework_name,
          requirement_key: `custom_${Date.now()}`,
          label: req.label,
          description: req.description || null,
          priority: req.priority || null,
          is_compliant: false,
          is_custom: true,
          is_active: true,
          notes: null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coe-regulatory-compliance', organizationId] });
      toast({ title: 'Requisito agregado', description: 'El requisito se ha agregado correctamente.' });
    },
  });

  const deleteRequirement = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('coe_regulatory_compliance')
        .delete()
        .eq('id', id)
        .eq('is_custom', true);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coe-regulatory-compliance', organizationId] });
      toast({ title: 'Requisito eliminado', description: 'El requisito personalizado se ha eliminado.' });
    },
  });

  return {
    compliance,
    loading: isLoading,
    toggleCompliance: toggleCompliance.mutate,
    updateNotes: updateNotes.mutate,
    addRequirement: addRequirement.mutate,
    deleteRequirement: deleteRequirement.mutate,
    frameworks: REGULATORY_FRAMEWORKS,
  };
}

// ========== useCoEFrameworkSelection ==========
interface FrameworkSelection {
  id?: string;
  organization_id: string;
  framework_name: string;
  is_active: boolean;
}

export function useCoEFrameworkSelection(organizationId: string) {
  const queryClient = useQueryClient();
  const { roles } = useAuth();
  const canSeedDefaults = roles.includes('admin') || roles.includes('super_admin');

  const { data: selections = [], isLoading } = useQuery({
    queryKey: ['coe-framework-selection', organizationId, canSeedDefaults],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('coe_framework_selection')
        .select('*')
        .eq('organization_id', organizationId);

      if (error) throw error;

      // Initialize if empty (solo admins pueden escribir por RLS; para el resto
      // devolvemos lista vacía en vez de romper la consulta con un error de RLS)
      if ((!data || data.length === 0) && canSeedDefaults) {
        const defaults = ['EU AI Act', 'GDPR', 'ISO/IEC 42001'].map(name => ({
          organization_id: organizationId,
          framework_name: name,
          is_active: true,
        }));

        const { data: inserted, error: insertError } = await supabase
          .from('coe_framework_selection')
          .insert(defaults)
          .select();

        if (insertError) throw insertError;
        return inserted as FrameworkSelection[];
      }

      return data as FrameworkSelection[];
    },
    enabled: !!organizationId && organizationId !== 'all',
  });

  const toggleFramework = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('coe_framework_selection')
        .update({ is_active: !isActive })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coe-framework-selection', organizationId] });
    },
  });

  return {
    selections,
    loading: isLoading,
    toggleFramework: toggleFramework.mutate,
    isFrameworkActive: (frameworkName: string) => {
      const sel = selections.find(s => s.framework_name === frameworkName);
      return sel ? sel.is_active : true;
    },
  };
}

// ========== useCoERoleAssignments ==========
interface CoERoleAssignment {
  id?: string;
  organization_id: string;
  role_key: string;
  assigned_name: string | null;
  assigned_email: string | null;
  status: string;
  notes: string | null;
}

export function useCoERoleAssignments(organizationId: string) {
  const queryClient = useQueryClient();

  const { data: assignments = [], isLoading } = useQuery({
    queryKey: ['coe-role-assignments', organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('coe_role_assignments')
        .select('*')
        .eq('organization_id', organizationId);

      if (error) throw error;
      return data as CoERoleAssignment[];
    },
    enabled: !!organizationId && organizationId !== 'all',
  });

  const upsertAssignment = useMutation({
    mutationFn: async (assignment: Omit<CoERoleAssignment, 'id'>) => {
      const { data, error } = await supabase
        .from('coe_role_assignments')
        .upsert(assignment, { onConflict: 'organization_id,role_key' })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coe-role-assignments', organizationId] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error al guardar',
        description: error.message || 'No se pudo guardar la asignación.',
        variant: 'destructive',
      });
    },
  });

  return {
    assignments,
    loading: isLoading,
    upsertAssignment: upsertAssignment.mutate,
  };
}

// ========== useCoEProcesses ==========
interface CoEProcess {
  id?: string;
  organization_id: string;
  name: string;
  description: string | null;
  maturity_level: number | null;
  sort_order: number | null;
}

export function useCoEProcesses(organizationId: string) {
  const queryClient = useQueryClient();

  const { data: processes = [], isLoading } = useQuery({
    queryKey: ['coe-processes', organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('coe_processes')
        .select('*')
        .eq('organization_id', organizationId)
        .order('sort_order');

      if (error) throw error;
      return data as CoEProcess[];
    },
    enabled: !!organizationId && organizationId !== 'all',
  });

  const addProcess = useMutation({
    mutationFn: async (process: Omit<CoEProcess, 'id' | 'organization_id'> & { category?: string; reference?: string; is_custom?: boolean; priority?: string }) => {
      const { data, error } = await supabase
        .from('coe_processes')
        .insert({ ...process, organization_id: organizationId })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coe-processes', organizationId] });
      toast({ title: 'Proceso agregado', description: 'El proceso se ha agregado correctamente.' });
    },
  });

  const updateMaturity = useMutation({
    mutationFn: async ({ id, maturity_level }: { id: string; maturity_level: number }) => {
      const { error } = await supabase
        .from('coe_processes')
        .update({ maturity_level })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coe-processes', organizationId] });
    },
  });

  const deleteProcess = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('coe_processes')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coe-processes', organizationId] });
      toast({ title: 'Proceso eliminado', description: 'El proceso se ha eliminado correctamente.' });
    },
  });

  return {
    processes,
    loading: isLoading,
    addProcess: addProcess.mutate,
    updateMaturity: updateMaturity.mutate,
    deleteProcess: deleteProcess.mutate,
  };
}

// ========== useCoERoleDefinitions ==========
export interface CoERoleDefinition {
  id?: string;
  organization_id: string;
  role_key: string;
  title: string;
  seniority: string | null;
  icon_name: string | null;
  color: string | null;
  responsibilities: string[];
  skills: string[];
  is_default: boolean;
  sort_order: number;
}

const DEFAULT_ROLE_DEFINITIONS: Omit<CoERoleDefinition, 'id' | 'organization_id'>[] = [
  {
    role_key: 'ai-director', title: 'Director de IA (Chief AI Officer)', seniority: 'Ejecutivo Senior',
    icon_name: 'Brain', color: 'bg-purple-500',
    responsibilities: [
      'Definir la visión y estrategia de IA alineada con objetivos de negocio',
      'Reportar al C-Suite y Board sobre iniciativas de IA',
      'Priorizar inversiones y casos de uso',
      'Evangelizar la cultura de IA en la organización',
    ],
    skills: ['Liderazgo ejecutivo', 'Visión estratégica', 'Comunicación C-level', 'Conocimiento técnico de IA'],
    is_default: true, sort_order: 1,
  },
  {
    role_key: 'tech-lead', title: 'Líder de Tecnología IA', seniority: 'Director/VP Técnico',
    icon_name: 'Target', color: 'bg-blue-500',
    responsibilities: [
      'Definir arquitectura técnica y estándares',
      'Evaluar y seleccionar plataformas y herramientas',
      'Supervisar MLOps y ciclo de vida de modelos',
      'Garantizar escalabilidad y rendimiento',
    ],
    skills: ['Arquitectura de ML', 'Cloud & MLOps', 'Evaluación de vendors', 'Liderazgo técnico'],
    is_default: true, sort_order: 2,
  },
  {
    role_key: 'governance-lead', title: 'Líder de Gobernanza IA', seniority: 'Director',
    icon_name: 'Shield', color: 'bg-red-500',
    responsibilities: [
      'Desarrollar políticas de IA responsable',
      'Gestionar riesgos y cumplimiento regulatorio',
      'Implementar marcos de auditoría de modelos',
      'Coordinar con Legal y Compliance',
    ],
    skills: ['Gestión de riesgos', 'Regulación de IA', 'Ética de IA', 'Auditoría'],
    is_default: true, sort_order: 3,
  },
  {
    role_key: 'talent-lead', title: 'Líder de Talento IA', seniority: 'Director',
    icon_name: 'Users', color: 'bg-green-500',
    responsibilities: [
      'Diseñar rutas de upskilling y reskilling',
      'Atraer y retener talento especializado',
      'Desarrollar rutas de carrera en IA',
      'Medir competencias y brechas',
    ],
    skills: ['Desarrollo de talento', 'Diseño curricular', 'Reclutamiento tech', 'Change management'],
    is_default: true, sort_order: 4,
  },
  {
    role_key: 'innovation-lead', title: 'Líder de Innovación IA', seniority: 'Director',
    icon_name: 'Lightbulb', color: 'bg-yellow-500',
    responsibilities: [
      'Identificar casos de uso de alto impacto',
      'Gestionar pipeline de experimentos',
      'Facilitar ideación y design thinking',
      'Monitorear tendencias y tecnologías emergentes',
    ],
    skills: ['Design thinking', 'Gestión de innovación', 'Análisis de tendencias', 'Facilitación'],
    is_default: true, sort_order: 5,
  },
  {
    role_key: 'business-lead', title: 'Líder de Negocio / Sponsor Ejecutivo', seniority: 'VP/SVP de Negocio',
    icon_name: 'Briefcase', color: 'bg-orange-500',
    responsibilities: [
      'Representar intereses del negocio en el CoE',
      'Validar ROI y valor de iniciativas',
      'Facilitar adopción en unidades de negocio',
      'Conectar con Junta Directiva',
    ],
    skills: ['Conocimiento del negocio', 'Gestión de stakeholders', 'Análisis financiero', 'Liderazgo'],
    is_default: true, sort_order: 6,
  },
];

export function useCoERoleDefinitions(organizationId: string) {
  const queryClient = useQueryClient();

  const { data: roles = [], isLoading } = useQuery({
    queryKey: ['coe-role-definitions', organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('coe_role_definitions' as any)
        .select('*')
        .eq('organization_id', organizationId)
        .order('sort_order');

      if (error) throw error;

      if (!data || data.length === 0) {
        const seeded = DEFAULT_ROLE_DEFINITIONS.map(r => ({ ...r, organization_id: organizationId }));
        const { data: inserted, error: insertError } = await supabase
          .from('coe_role_definitions' as any)
          .insert(seeded)
          .select();
        if (insertError) throw insertError;
        return (inserted || []) as unknown as CoERoleDefinition[];
      }

      return data as unknown as CoERoleDefinition[];
    },
    enabled: !!organizationId && organizationId !== 'all',
  });

  const addRole = useMutation({
    mutationFn: async (role: Omit<CoERoleDefinition, 'id' | 'organization_id' | 'is_default' | 'sort_order'> & { sort_order?: number }) => {
      const role_key = role.role_key || `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const { data, error } = await supabase
        .from('coe_role_definitions' as any)
        .insert({
          ...role,
          role_key,
          organization_id: organizationId,
          is_default: false,
          sort_order: role.sort_order ?? roles.length + 1,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coe-role-definitions', organizationId] });
      toast({ title: 'Rol agregado', description: 'El rol se ha agregado correctamente.' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const updateRole = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<CoERoleDefinition> }) => {
      const { error } = await supabase
        .from('coe_role_definitions' as any)
        .update(updates)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coe-role-definitions', organizationId] });
      toast({ title: 'Rol actualizado' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const deleteRole = useMutation({
    mutationFn: async ({ id, role_key }: { id: string; role_key: string }) => {
      const { error } = await supabase
        .from('coe_role_definitions' as any)
        .delete()
        .eq('id', id);
      if (error) throw error;
      // Cleanup associated assignment
      await supabase
        .from('coe_role_assignments')
        .delete()
        .eq('organization_id', organizationId)
        .eq('role_key', role_key);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coe-role-definitions', organizationId] });
      queryClient.invalidateQueries({ queryKey: ['coe-role-assignments', organizationId] });
      toast({ title: 'Rol eliminado' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  return {
    roles,
    loading: isLoading,
    addRole: addRole.mutate,
    updateRole: updateRole.mutate,
    deleteRole: deleteRole.mutate,
  };
}
